import { FC, useEffect, useState } from 'react';
import ReactFileReader from 'react-file-reader';
import { useTranslation } from 'react-i18next';
import { useUnit } from 'effector-react';
import { Field } from 'formik';
import {
  useMediaQuery,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  Link,
} from '@mui/material';
import {
  Add,
  AttachFile,
  DeleteOutline,
  InsertDriveFileOutlined,
  Link as LinkIcon,
} from '@mui/icons-material';
import { InvoicesStatusTypeSelectField } from '@features/tickets-invoices-status-select';
import { getStatusName } from '@features/tickets/libs';
import { InputField } from '@ui/index';
import { DownloadButton } from '../../atoms';
import { Ticket, FileInvoice } from '../../interfaces';
import { $mode, uploadAttachment, $attachment, downloadFile } from '../../models';
import {
  useStyles,
  StyledChip,
  StyledDeleteButton,
  StyledAddButton,
  StyledButtonText,
  StyledAttachFileButton,
  StyledLinkWrapper,
  StyledLinkContainer,
  StyledDivider,
} from './styles';

interface Props {
  values: Ticket;
  push: (entity: object) => void;
  remove: (index: number) => void;
  setFieldValue: (name: string, value: any) => void;
  errors: { invoices: { [key: number]: string }[] };
}

interface InvoiceItem {
  id?: number;
  file: { id: number; name: string } | FileInvoice;
  amount: number;
  status: string;
  url: string;
  type: string;
}

const invoiceEntityFile = {
  id: null,
  file: null,
  amount: '',
  status: '',
  url: '',
  type: 'file',
};

const invoiceEntityUrl = {
  id: null,
  file: null,
  amount: '',
  status: '',
  url: '',
  type: 'url',
};

export const TicketInvoiceItem: FC<Props> = ({
  push,
  remove,
  values,
  setFieldValue,
  errors,
}) => {
  const { t } = useTranslation();
  const [mode, attachment] = useUnit([$mode, $attachment]);
  const classes = useStyles({ mode });
  const isScreenLess = useMediaQuery('(max-width:1320px)');
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);

  useEffect(() => {
    if (attachment) {
      setFieldValue(`invoices[${attachment.index}].file.id`, attachment.id);
      setFieldValue(`invoices[${attachment.index}].file.name`, attachment.name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachment]);

  const onClick = (event: React.MouseEvent<HTMLButtonElement>) =>
    setAnchorEl(event.currentTarget);

  const handleClose = () => setAnchorEl(null);

  const onClickAddUrl = () => {
    push(invoiceEntityUrl);
    handleClose();
  };

  const onClickAddFile = () => {
    push(invoiceEntityFile);
    handleClose();
  };

  const invoices = Array.isArray(values?.invoices)
    ? values.invoices.map((item: InvoiceItem, index) => {
        const name = `invoices[${index}]`;
        const urlMode = item?.id ? 'view' : 'edit';

        const invoiceError = Array.isArray(errors?.invoices)
          ? errors.invoices[index]
          : false;

        const onDelete = () => remove(index);
        const handleDownload = () => downloadFile(item.file as FileInvoice);

        const handleFiles = (files: File[]) => {
          uploadAttachment({ file: files[0], index });
        };

        const handleAmount = (e: { target: { value: string } }) => {
          const { value } = e.target;
          const splittedValue = value.split('.');

          if (splittedValue[0] && splittedValue[1] && splittedValue[1].length > 2) {
            const newValue = `${splittedValue[0]}.${splittedValue[1].substring(0, 2)}`;

            return setFieldValue(`${name}.amount`, Number.parseFloat(newValue));
          }

          if (!Number.isNaN(Number.parseFloat(value))) {
            return setFieldValue(`${name}.amount`, Number.parseFloat(value));
          }

          return setFieldValue(`${name}.amount`, value);
        };

        const handleKeyDown = (e: { keyCode: number; preventDefault: () => void }) => {
          if (e.keyCode === 110 || e.keyCode === 190) {
            e.preventDefault();
          }
        };

        const handleUrl = ({ target: { value } }: { target: { value: string } }) =>
          setFieldValue(`${name}.url`, value);

        const statusName = getStatusName(item?.status);

        const fields = (
          <div className={classes.invoiceItem}>
            <div className={classes.fields}>
              {item?.file && item?.type === 'file' ? (
                <StyledLinkWrapper>
                  <DownloadButton onClick={handleDownload} />
                  <Typography
                    noWrap
                    title={item.file.name}
                    classes={{ root: classes.fileName }}
                  >
                    {item.file.name}
                  </Typography>
                </StyledLinkWrapper>
              ) : (
                item?.type === 'file' && (
                  <StyledLinkWrapper file>
                    <ReactFileReader fileTypes="*" handleFiles={handleFiles}>
                      <StyledAttachFileButton
                        variant="outlined"
                        startIcon={<AttachFile />}
                      >
                        <StyledButtonText>{t('AttachFile')}</StyledButtonText>
                      </StyledAttachFileButton>
                    </ReactFileReader>
                    {Boolean((invoiceError as { file: string })?.file) && (
                      <Typography classes={{ root: classes.errorFile }}>
                        {(invoiceError as { file: string }).file}
                      </Typography>
                    )}
                  </StyledLinkWrapper>
                )
              )}
              {item?.type === 'url' && (
                <StyledLinkWrapper>
                  <StyledLinkContainer>
                    {urlMode === 'view' ? (
                      <Link href={item.url} underline="always">
                        {item.url}
                      </Link>
                    ) : (
                      <Field
                        name={`${name}.url`}
                        label={null}
                        placeholder={t('EnterLink')}
                        mode="edit"
                        component={InputField}
                        divider={false}
                        onChange={handleUrl}
                      />
                    )}
                  </StyledLinkContainer>
                </StyledLinkWrapper>
              )}
              <div>
                <Field
                  name={`${name}.amount`}
                  label={null}
                  placeholder={t('AmountPayable')}
                  mode={mode}
                  component={InputField}
                  divider={false}
                  type="number"
                  onKeyDown={handleKeyDown}
                  onChange={handleAmount}
                />
              </div>
              {item?.id && (
                <div>
                  {mode === 'edit' ? (
                    <Field
                      name={`${name}.status`}
                      label={null}
                      placeholder={t('selectStatuse')}
                      mode="edit"
                      component={InvoicesStatusTypeSelectField}
                      divider={false}
                    />
                  ) : (
                    <StyledChip label={statusName} status={item?.status} />
                  )}
                </div>
              )}
            </div>
            {mode === 'edit' && (
              <StyledDeleteButton onClick={onDelete} isScreenLess={isScreenLess}>
                <DeleteOutline />
              </StyledDeleteButton>
            )}
          </div>
        );

        return (
          <Field
            key={index}
            name={name}
            render={() => <div className={classes.invoice}>{fields}</div>}
          />
        );
      })
    : null;

  const addButton = mode === 'edit' && (
    <>
      <StyledAddButton onClick={onClick} variant="outlined" startIcon={<Add />}>
        <StyledButtonText>{t('add')}</StyledButtonText>
      </StyledAddButton>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <MenuItem onClick={onClickAddUrl}>
          <ListItemIcon>
            <LinkIcon />
          </ListItemIcon>
          {t('Link')}
        </MenuItem>
        <MenuItem onClick={onClickAddFile}>
          <ListItemIcon>
            <InsertDriveFileOutlined />
          </ListItemIcon>
          {t('File')}
        </MenuItem>
      </Menu>
    </>
  );

  return (
    <>
      {invoices}
      {addButton}
      <StyledDivider />
    </>
  );
};
